import React from 'react';
import { motion } from 'framer-motion';
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaYoutube } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import PolicyModal from './PolicyModal';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [policyModal, setPolicyModal] = React.useState({ isOpen: false, type: null });

  const openPolicyModal = (type) => {
    setPolicyModal({ isOpen: true, type });
  };

  const footerLinks = {
    [t('footer.services')]: [
      { name: 'Smart Home Automation', href: '#services' },
      { name: 'Security Systems', href: '#services' },
      { name: 'Intelligent Lighting', href: '#services' },
      { name: 'Climate Control', href: '#services' },
    ],
    [t('footer.company')]: [
      { name: t('nav.about'), href: '#about' },
      { name: t('nav.projects'), href: '#projects' },
      { name: t('nav.contact'), href: '#contact' },
      { name: 'Careers', href: '#' },
    ],
    [t('footer.businessHours')]: [
      { name: t('footer.monFri'), href: '#' },
      { name: t('footer.saturday'), href: '#' },
      { name: t('footer.sunday'), href: '#' },
    ],
  };

  const socialLinks = [
    { icon: FaFacebook, href: '#', color: '#1877f2' },
    { icon: FaTwitter, href: '#', color: '#1da1f2' },
    { icon: FaInstagram, href: '#', color: '#e4405f' },
    { icon: FaLinkedin, href: '#', color: '#0077b5' },
    { icon: FaYoutube, href: '#', color: '#ff0000' },
  ];

  return (
    <footer className="bg-gradient-to-b from-[#0a0e27] to-[#050811] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none opacity-50">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-[#00d4ff]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#0099ff]/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Logo and Description */}
          <div className="lg:col-span-2">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center mb-4"
            >
              <img 
                src="/logo-new.png" 
                alt="Sheba Smart Home" 
                className="h-16 w-auto"
              />
            </motion.div>
            <p className="text-white/60 mb-6 leading-relaxed">
              {t('footer.description')}
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  whileHover={{ scale: 1.1, y: -2 }}
                  className="w-10 h-10 rounded-xl glass-strong flex items-center justify-center group"
                  style={{ backgroundColor: `${social.color}20` }}
                >
                  <social.icon
                    className="text-white/70 group-hover:text-white transition-colors"
                    style={{ color: social.color }}
                  />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="text-white font-semibold text-lg mb-4">{title}</h4>
              <ul className="space-y-2">
                {links.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-white/60 hover:text-[#00d4ff] transition-colors inline-block"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter */}
        <div className="border-t border-white/10 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="text-white font-semibold text-lg mb-2">
                {t('footer.newsletter')}
              </h4>
              <p className="text-white/60 text-sm">
                {t('footer.newsletterDesc')}
              </p>
            </div>
            <div className="flex w-full md:w-auto">
              <input
                type="email"
                placeholder={t('footer.emailPlaceholder')}
                className="flex-1 md:w-64 px-4 py-3 bg-white/5 border border-white/10 rounded-l-xl text-white placeholder-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors"
              />
              <button className="btn-primary !rounded-l-none !rounded-r-xl">
                {t('footer.subscribe')}
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-sm text-center md:text-left">
              © {currentYear} Sheba Smart Home. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <span className="text-white/40 hidden md:inline">Built with React & FastAPI</span>
              <button 
                onClick={() => openPolicyModal('terms')}
                className="text-white/60 hover:text-[#00d4ff] transition-colors"
              >
                {t('footer.terms')}
              </button>
              <button 
                onClick={() => openPolicyModal('privacy')}
                className="text-white/60 hover:text-[#00d4ff] transition-colors"
              >
                {t('footer.privacy')}
              </button>
              <button 
                onClick={() => openPolicyModal('cookies')}
                className="text-white/60 hover:text-[#00d4ff] transition-colors"
              >
                {t('footer.cookies')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Policy Modal */}
      <PolicyModal
        isOpen={policyModal.isOpen}
        onClose={() => setPolicyModal({ isOpen: false, type: null })}
        type={policyModal.type}
      />
    </footer>
  );
};

export default Footer;